/**
 * Schema.org ItemList JSON-LD — for catalog/index pages. Tells Google
 * + AI search "this page contains these items in this order."
 *
 * <p>Items can be plain URL strings or {@code { url, name }} objects.
 * Position numbers are 1-indexed and assigned automatically.
 */
import { JsonLdScript } from './_shared';

export type ItemListEntry = string | { url: string; name?: string };

export interface ItemListSchemaJsonLdProps {
  name?: string;
  description?: string;
  items: ItemListEntry[];
  id?: string;
}

export function ItemListSchemaJsonLd({ id, ...input }: ItemListSchemaJsonLdProps) {
  const schema = buildItemListSchema(input);
  if (!schema) return null;
  return <JsonLdScript schema={schema} id={id ?? 'schema-itemlist'} />;
}

export function buildItemListSchema(input: Omit<ItemListSchemaJsonLdProps, 'id'>) {
  if (!input.items || input.items.length === 0) return null;
  const itemListElement = input.items.map((entry, i) => {
    const url = typeof entry === 'string' ? entry : entry.url;
    const name = typeof entry === 'string' ? undefined : entry.name;
    return {
      '@type': 'ListItem',
      position: i + 1,
      url,
      name,
    };
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: input.name,
    description: input.description,
    numberOfItems: input.items.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement,
  };
}
